import React, {useState} from "react";
import Grid from "@mui/material/Grid";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import AddIcon from "@mui/icons-material/Add";
import {Tab, Tabs} from "@mui/material";
import ReportIcon from "@mui/icons-material/Report";
import ManageSearchIcon from "@mui/icons-material/ManageSearch";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import InsightsIcon from "@mui/icons-material/Insights";
import StackedBarChartIcon from "@mui/icons-material/StackedBarChart";
import {useTheme} from "@mui/material/styles";
import {useTranslation} from "react-i18next";
import {NavLink, Link, useLocation} from "react-router-dom";
import {UserContext} from "../../App";

export default function ExperimentHeader() {

    const theme = useTheme();
    const {t} = useTranslation();

    const {
        datasets,
        experiments,
        currentDatasetIdx,
        setCurrentDatasetIdx,
        currentExperimentIdx,
        setCurrentExperimentIdx,
    } = React.useContext(UserContext);

    const handleDatasetChange = (event) => {
        const datasetIndex = event.target.value;
        // set context variable
        setCurrentDatasetIdx(datasetIndex);
    };
    const handleExperimentChange = (event) => {
        const experimentIndex = event.target.value;
        setCurrentExperimentIdx(experimentIndex);
    };

    const isDatasetSelected = currentDatasetIdx !== null;

    const location = useLocation();
    // get Experiment Progress to display only the available steps
    const {
        currentExperimentProgress
    } = React.useContext(UserContext);

    const isActiveRoute = (route) => {
        // simple method to check, whether a route is active
        // needed to mark listItems as selected
        const currentPath = location.pathname;
        return currentPath.includes(route);
    };

    const [selectedTab, setSelectedTab] = useState(0);
    const handleTabChange = (event, newValue) => {
        setSelectedTab(newValue);
    };

    return (
        <Grid
            id="experimentAnalyze-container"
            container
            alignItems="flex-start"
            justifyContent='flex-start'
        >
            <Grid
                id="datasetExperimentSelector-container"
                container
                justifyContent="center"
                sx={{
                    gap: 2,
                }}
            >
                <Grid
                    id="dataset-selector-container"
                    item xl={2} lg={4} md={6} xs={12}
                    justifyContent="center"
                    sx={{
                        color: 'black',
                        borderRadius: '16px',
                        backgroundColor: '#e3e3e3',
                        padding: theme.spacing(1, 2),
                        display: 'flex',
                        justifyContent: 'space-between',
                    }}

                >
                    <FormControl
                        variant="filled"
                    >
                        <InputLabel>
                            {t("currentExperimentSelector.CurrentDatasetDropdown")}
                        </InputLabel>
                        <Select
                            value={currentDatasetIdx}
                            id="select-ds"
                            onChange={handleDatasetChange}
                            displayEmpty
                            label={t("currentExperimentSelector.CurrentDatasetDropdown")}
                            sx={{
                                '& .MuiOutlinedInput-notchedOutline': {
                                    borderColor: 'transparent',
                                },
                                '&.MuiFilledInput-root::before': {
                                    display: 'none',
                                },
                                '& .MuiSelect-icon': {display: 'none'}
                            }}
                            className="currentDatasetSelector"
                            style={{
                                backgroundColor: "transparent"
                            }}
                        >
                            {datasets.length ? (
                                datasets.map((ds, index) => (
                                    /* Using the index as value here, to get an easier mapping for the onChange method */
                                    <MenuItem key={`ds-${index}`} value={index}>
                                        {ds.datasetName}
                                    </MenuItem>
                                ))
                            ) : (
                                <MenuItem value={0}>
                                    <em>{t("currentExperimentSelector.NoDataset")}</em>
                                </MenuItem>
                            )}
                        </Select>
                    </FormControl>
                    <Link to="/new-dataset" id="add-dataset-topbar-link">
                        <Tooltip title={t("currentExperimentSelector.AddDatasetButton")}>
                            <IconButton
                                aria-label="add new Dataset"
                                className="addDatasetButton"
                                size="large"
                            >
                                <AddIcon fontSize="inherit"/>
                            </IconButton>
                        </Tooltip>
                    </Link>
                </Grid>
                <Grid
                    id="experiment-container"
                    justifyContent="center"
                    item
                    xl={2} lg={4} md={6} xs={12}
                    sx={{
                        color: 'black',
                        borderRadius: '16px',
                        backgroundColor: experiments.length > 0 ? '#e3e3e3' : '#f1f1f1',
                        padding: theme.spacing(1, 2),
                        display: 'flex',
                        justifyContent: 'space-between',
                    }}
                >
                    <FormControl variant="filled">
                        <InputLabel id="select-exp-label">
                            {t("currentExperimentSelector.CurrentExperimentDropdown")}
                        </InputLabel>
                        <Select
                            labelId="select-exp-label"
                            id="select-exp"
                            value={currentExperimentIdx}
                            onChange={handleExperimentChange}
                            label={t("currentExperimentSelector.CurrentExperimentDropdown")}
                            sx={{
                                pointerEvents: experiments.length > 0 ? 'auto' : 'none',
                                opacity: experiments.length > 0 ? 1 : 0.5,
                                '& .MuiOutlinedInput-notchedOutline': {
                                    borderColor: 'transparent',
                                },
                                '&.MuiFilledInput-root::before': {
                                    display: 'none',
                                },
                                '& .MuiSelect-icon': {display: 'none'},
                            }}
                            className="currentExperimentSelector"
                            style={{
                                backgroundColor: "transparent"
                            }}
                        >
                            {experiments.length ? (
                                experiments.map((exp, index) => (
                                    <MenuItem key={`exp-${exp.experimentName}`} value={index}>
                                        {exp.experimentName}
                                    </MenuItem>
                                ))
                            ) : (
                                <MenuItem value={0}>
                                    <em>{t("currentExperimentSelector.NoExperiment")}</em>
                                </MenuItem>
                            )}
                        </Select>
                    </FormControl>
                    <Link to="/new-experiment" id="add-experiment-topbar-link">
                        <Tooltip title={t("currentExperimentSelector.AddExperimentButton")}>
                            <IconButton
                                aria-label="add new Experiment"
                                className="addExperimentButton"
                                disabled={!isDatasetSelected}
                                size='large'
                            >
                                <AddIcon fontSize="inherit"/>
                            </IconButton>
                        </Tooltip>
                    </Link>
                </Grid>
            </Grid>

            <Grid
                id="analyzeSelector-container"
                container
                sx={{
                    padding: theme.spacing(4, 0, 0, 0),
                    width: '100%'
                }}
            >
                <Grid
                    container
                    className="currentExperimentProgress"
                >
                    <Tabs
                        scrollButtons="auto"
                        aria-label="experiment-tabs"
                        value={selectedTab}
                        onChange={handleTabChange}
                        variant="fullWidth"
                        selected={
                            isActiveRoute("quality-check") ||
                            isActiveRoute("inspect") ||
                            isActiveRoute("train") ||
                            isActiveRoute("analyze") ||
                            isActiveRoute("runs")
                        }
                        sx={{
                            borderRadius: '16px',
                            width: '100%'
                        }}
                    >
                        <Tab
                            label={t("sidebar.DataQualityButton")}
                            key="Pogress"
                            disabled={!currentExperimentProgress?.processed || (experiments.length <= 0)}
                            selected={isActiveRoute("quality-check")}
                            icon={<ReportIcon/>}
                            component={NavLink}
                            to='/quality-check'
                            sx={{
                                border: isActiveRoute("quality-check") ? '2px solid #fff' : '2px solid #e3e3e3',
                                backgroundColor: isActiveRoute("quality-check") ? '#fff' : '#e3e3e3',
                                color: isActiveRoute("quality-check") ? '#000' : 'black',
                                '&:hover': {
                                    backgroundColor: '#16425B',
                                    color: 'white',
                                    opacity: 1,
                                }
                            }}
                        />
                        <Tab
                            label={t("sidebar.InspectButton")}
                            key="Inspect"
                            disabled={!currentExperimentProgress?.processed || (experiments.length <= 0)}
                            selected={isActiveRoute("inspect")}
                            icon={<ManageSearchIcon/>}
                            component={NavLink}
                            to="/inspect"
                            sx={{
                                border: isActiveRoute("inspect") ? '2px solid #fff' : '2px solid #e3e3e3',
                                backgroundColor: isActiveRoute("inspect") ? '#fff' : '#e3e3e3',
                                color: isActiveRoute("inspect") ? '#000' : 'black',
                                '&:hover': {
                                    backgroundColor: '#16425B',
                                    color: 'white',
                                    opacity: 1
                                }
                            }}
                        />
                        <Tab
                            key="train"
                            label={t("sidebar.ConfigureButton")}
                            disabled={!currentExperimentProgress?.processed || (experiments.length <= 0)}
                            icon={<PlayArrowIcon/>}
                            component={NavLink}
                            to="/train"
                            sx={{
                                border: isActiveRoute("train") ? '2px solid #fff' : '2px solid #e3e3e3',
                                backgroundColor: isActiveRoute("train") ? '#fff' : '#e3e3e3',
                                color: isActiveRoute("train") ? '#000' : 'black',
                                '&:hover': {
                                    backgroundColor: '#16425B',
                                    color: 'white',
                                    opacity: 1
                                }
                            }}
                        />
                        <Tab
                            key="analyze"
                            label={t("sidebar.AnalyseButton")}
                            disabled={!(currentExperimentProgress?.trained > 0) || (experiments.length <= 0)}
                            icon={<InsightsIcon/>}
                            component={NavLink}
                            to="/analyze"
                            sx={{
                                border: isActiveRoute("analyze") ? '2px solid #fff' : '2px solid #e3e3e3',
                                backgroundColor: isActiveRoute("analyze") ? '#fff' : '#e3e3e3',
                                color: isActiveRoute("analyze") ? '#000' : 'black',
                                '&:hover': {
                                    backgroundColor: '#16425B',
                                    color: 'white',
                                    opacity: 1
                                }
                            }}
                        />
                        <Tab
                            key="runs"
                            label={t("sidebar.CompareButton")}
                            disabled={!(currentExperimentProgress?.trained > 0) || (experiments.length <= 0)}
                            icon={<StackedBarChartIcon/>}
                            component={NavLink}
                            to="/runs"
                            sx={{
                                border: isActiveRoute("runs") ? '2px solid #fff' : '2px solid #e3e3e3',
                                backgroundColor: isActiveRoute("runs") ? '#fff' : '#e3e3e3',
                                color: isActiveRoute("runs") ? '#000' : 'black',
                                '&:hover': {
                                    backgroundColor: '#16425B',
                                    color: 'white',
                                    opacity: 1
                                }
                            }}
                        />
                    </Tabs>
                </Grid>
            </Grid>
        </Grid>
    )
}